/**
 * One loan tape workflow, kept current while it runs (DOP-481).
 *
 * **Why this polls rather than reads the SSE stream.** The live reader in
 * `pages/Chat.tsx` (`attachLiveStream`) is not a parser you can lift out: it
 * writes through `messagesRef`, `chatGenerationRef` and the per-generation seq
 * watermark that is the resume contract with the backend, and every one of
 * those is chat state. Extracting it would mean rewriting the chat's streaming
 * path to serve a page that needs one bit of information — "is it done yet" —
 * and none of the deltas. So this re-reads the thread every few seconds while
 * the newest run is pending or running, which is what the ticket allows, and
 * stops the moment it reaches a terminal state.
 *
 * A run takes minutes and produces blocks only at the end, so the poll costs
 * a handful of requests per run and the page shows the finished result within
 * one interval of it existing.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
	type LoanTapeDashboard,
	getLoanTapeDashboard,
	isLoanTapeRunLive,
	runLoanTapeDashboard,
} from "../services/loanTapeDashboardService";
import type { SchemaExtractorPayload } from "../components/SchemaExtractorForm";
import { friendlyErrorMessage } from "../utils/apiErrorDetail";

/** How often the thread is re-read while a run is in flight. */
export const LOAN_TAPE_POLL_MS = 4000;

export interface UseLoanTapeDashboard {
	dashboard: LoanTapeDashboard | null;
	loading: boolean;
	error: string | null;
	/** A run of this workflow is in flight. */
	isRunning: boolean;
	/** Start a run with the given config, or the stored one when omitted. */
	run: (payload?: SchemaExtractorPayload) => Promise<void>;
	refresh: () => void;
}

export function useLoanTapeDashboard(id: string): UseLoanTapeDashboard {
	const [dashboard, setDashboard] = useState<LoanTapeDashboard | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	// True only between Run now being pressed and the reload that shows the
	// new turn. After that the thread itself says a run is in flight, so the
	// flag is dropped rather than kept in sync with it.
	const [starting, setStarting] = useState(false);

	const isMounted = useRef(true);
	useEffect(() => {
		isMounted.current = true;
		return () => {
			isMounted.current = false;
		};
	}, []);

	// Every read is numbered and only the newest may land: a slow earlier
	// response must not overwrite a newer one (a finished run turning back
	// into a running one, and the poll never stopping). `inFlight` keeps the
	// poll from stacking reads on a slow connection.
	const latestRequest = useRef(0);
	const inFlight = useRef(false);

	const load = useCallback(async () => {
		const request = ++latestRequest.current;
		inFlight.current = true;
		try {
			const next = await getLoanTapeDashboard(id);
			if (!isMounted.current || request !== latestRequest.current) return;
			setDashboard(next);
			setError(null);
		} catch (caught) {
			if (!isMounted.current || request !== latestRequest.current) return;
			// The rows already on screen stay: a transient 502 does not un-run
			// the extraction the user is looking at.
			setError(friendlyErrorMessage(caught, "Could not load this workflow."));
		} finally {
			if (request === latestRequest.current) {
				inFlight.current = false;
				if (isMounted.current) setLoading(false);
			}
		}
	}, [id]);

	useEffect(() => {
		setLoading(true);
		setDashboard(null);
		void load();
	}, [load]);

	const isRunning =
		starting || isLoanTapeRunLive(dashboard?.latest?.status ?? null);

	useEffect(() => {
		if (!isRunning) return;
		const timer = window.setInterval(() => {
			if (!inFlight.current) void load();
		}, LOAN_TAPE_POLL_MS);
		return () => window.clearInterval(timer);
	}, [isRunning, load]);

	const run = useCallback(
		async (payload?: SchemaExtractorPayload) => {
			const config = payload ?? dashboard?.config ?? null;
			if (!config || isRunning) return;
			setStarting(true);
			try {
				await runLoanTapeDashboard(id, config);
				await load();
			} catch (caught) {
				if (!isMounted.current) return;
				setError(friendlyErrorMessage(caught, "Could not start the run."));
			} finally {
				if (isMounted.current) setStarting(false);
			}
		},
		[dashboard, id, isRunning, load],
	);

	const refresh = useCallback(() => {
		void load();
	}, [load]);

	return { dashboard, loading, error, isRunning, run, refresh };
}
